import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import Form from "../../../components/Form";
import { SubmitHandler, FormHandles } from "@unform/core";
import ConsultarService, { ResponseParcelamentos } from "../../../services/ConsultarService";
import Input, { MaterialTextFieldElement } from "../../../components/v2/Input";
import Button from "../../../components/Button";
import Layout from "../../../components/v2/Layout";
import ItemParcelamento from "../../../components/v2/ItemParcelamento";
import { useQuery } from "../../../helpers/Utils";
import { isCpfOrCnpj } from "../../../helpers/Validates";
import { cpfOrCnpjMask, normalizeCpfOrCnpj, onlyAlphanumeric } from "../../../helpers/Masks";
import { isServerError } from "../../../config/api";
import style from "./styles.module.scss";

interface FormData {
  documento: string;
  valor: string;
}

function normalizePaymentRequestStatus(status: string): string {
  return status
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

const keyI18n = "translations.consultInstallments.view";

export default function ConsultarParcelamentos() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [data, setData] = useState<ResponseParcelamentos>();
  const [hasSearchError, setHasSearchError] = useState(false);
  const [documentError, setDocumentError] = useState("");
  const [contributorDocument, setContributorDocument] = useState('');
  const [searchedByInstallment, setSearchedByInstallment] = useState(false);

  const contributorName = data?.contributor_name_without_mask;
  const paymentRequestWithWarning = data?.payment_requests.find((paymentRequest) => {
    const normalizedStatus = normalizePaymentRequestStatus(
      `${paymentRequest.status} ${paymentRequest.status_pt_br}`,
    );

    return ["canceled", "cancelled", "cancelado", "lost", "perdido"]
      .some((status) => normalizedStatus.includes(status));
  });
  const normalizedWarningStatus = paymentRequestWithWarning
    ? normalizePaymentRequestStatus(
      `${paymentRequestWithWarning.status} ${paymentRequestWithWarning.status_pt_br}`,
    )
    : "";
  const paymentRequestWarning = normalizedWarningStatus.includes("cancel")
    ? t(`${keyI18n}.warnings.canceled`)
    : normalizedWarningStatus.includes("lost") || normalizedWarningStatus.includes("perdido")
      ? t(`${keyI18n}.warnings.lost`)
      : "";
  const formRef = useRef<FormHandles>(null);
  const documentInputRef = useRef<MaterialTextFieldElement>(null);
  const installmentInputRef = useRef<MaterialTextFieldElement>(null);

  const query = useQuery();
  const d = query.get("d");
  const n = query.get("n");

  const submitAttendance = async (documento: string) => {
    const contributor_document = normalizeCpfOrCnpj(documento);
    const contributor_attendance_histories = {'attendance_place':'Portal do Contribuinte - Consultar Parcelamento',
                                              'attendant':'Assitente Virtual',
                                              'attendance_subject':'portal_of_taxpayer_installment_consult'};

    const attendanceData = {contributor_document, contributor_attendance_histories}

    await ConsultarService
      .attendanceRegistration(attendanceData)

  };

  const handleSearchInstallments = async (documento: string, valor: string) => {
    const normalizedDocument = normalizeCpfOrCnpj(documento);
    const normalizedInstallment = onlyAlphanumeric(valor).unmask();

    setData(undefined);
    setHasSearchError(false);
    setIsFormCollapsed(true);
    setLoading(true);
    setHasSearched(true);
    setContributorDocument(normalizedDocument);
    setSearchedByInstallment(Boolean(normalizedInstallment));

    await ConsultarService
      .parcelamentos(normalizedDocument, normalizedInstallment)
      .then((response) => {
        if (response.is_updated){
          void submitAttendance(normalizedDocument).catch(console.error);
        };

        if (response.payment_requests.length === 0) {
          setHasSearchError(true);
          setIsFormCollapsed(false);
          return;
        }

        setData(response);
      }).catch((err) => {
        setHasSearchError(true);
        setIsFormCollapsed(false);
        if (isServerError(err)) {
          if (err.error == "Não foram encontrados parcelamentos ativos."){
            void submitAttendance(normalizedDocument).catch(console.error);
          }
        } else {
          console.error(err);
        }
      }).finally(() => {
        setLoading(false);
      });
  };

  const handleSubmit: SubmitHandler<FormData> = async () => {
    const formData: FormData = {
      documento: normalizeCpfOrCnpj(documentInputRef.current?.value || ""),
      valor: onlyAlphanumeric(installmentInputRef.current?.value || "").unmask(),
    };

    try {
      setHasSearchError(false);
      setDocumentError("");

      const schema = Yup.object<FormData>().shape({
        documento: Yup.string()
          .test("cpfOrCnpj", t(`${keyI18n}.validation.invalidDocument`), (value) => {
            if (typeof value === "string") {
              return isCpfOrCnpj(value);
            }

            return false;
          })
          .required(t(`${keyI18n}.validation.requiredDocument`)),
      });

      await schema.validate(formData, {
        abortEarly: false,
      });

      await handleSearchInstallments(formData.documento, formData.valor);
    } catch (err) {
      setLoading(false);
      if (err instanceof Yup.ValidationError) {
        const validationError = err.inner.find(({ path }) => path === "documento");
        setDocumentError(validationError?.message || "");
      }
    }
  };

  const handleEffectSubmit = async () => {
    if (documentInputRef.current && installmentInputRef.current && d && n) {
      documentInputRef.current.value = cpfOrCnpjMask(d).mask();
      installmentInputRef.current.value = onlyAlphanumeric(n).mask();
      documentInputRef.current.dispatchEvent(new Event("input", { bubbles: true, composed: true }));
      installmentInputRef.current.dispatchEvent(new Event("input", { bubbles: true, composed: true }));
      await handleSearchInstallments(d, n);
    }
  };

  const handleFormKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const isComposing = (event.nativeEvent as KeyboardEvent).isComposing;
    const target = event.target as HTMLElement;
    const isTextField = Boolean(
      target.closest("md-filled-text-field, md-outlined-text-field"),
    );

    if (
      event.key !== "Enter"
      || event.shiftKey
      || event.ctrlKey
      || event.altKey
      || event.metaKey
      || isComposing
      || loading
      || !isTextField
    ) {
      return;
    }

    event.preventDefault();
    formRef.current?.submitForm();
  };

  useEffect(() => {
    handleEffectSubmit();
  }, [d, n]);

  return (
    <Layout>
      <section
        className={[
          style.searchContainer,
          isFormCollapsed ? style.searchContainerCollapsed : "",
        ].filter(Boolean).join(" ")}
      >
        <div
          className={[
            style.titleContainer,
            isFormCollapsed ? style.titleContainerCollapsed : "",
          ].filter(Boolean).join(" ")}
        >
          <div className={style.titleText}>
            <span className={`${style.title} pgm-title-large`}>
              {t(`${keyI18n}.title`)}
            </span>
            {!isFormCollapsed && (
              <p className={`${style.subtitle} pgm-body-large`}>
                {t(`${keyI18n}.description`)}
              </p>
            )}
          </div>
          <Button
            variant="text"
            type="button"
            iconButton
            className={style.collapseButton}
            aria-label={isFormCollapsed
              ? t(`${keyI18n}.aria.expandForm`)
              : t(`${keyI18n}.aria.collapseForm`)}
            aria-controls="consult-installments-form"
            aria-expanded={!isFormCollapsed}
            onClick={() => setIsFormCollapsed((collapsed) => !collapsed)}
            tooltip={isFormCollapsed
              ? t(`${keyI18n}.tooltips.maximizeForm`)
              : t(`${keyI18n}.tooltips.minimizeForm`)}
            tooltipPlacement="bottom"
            icon={(
              <span className={`material-symbols-sharp ${style.collapseIcon}`}>
                {isFormCollapsed ? "arrow_drop_up" : "arrow_drop_down"}
              </span>
            )}
          />
        </div>
        <Form
          id="consult-installments-form"
          className={style.form}
          ref={formRef}
          onSubmit={handleSubmit}
          onKeyDown={handleFormKeyDown}
          hidden={isFormCollapsed}
        >
          <Input
            ref={documentInputRef}
            className={style.formInput}
            fullWidth
            id="contributorDocment"
            label={t(`${keyI18n}.fields.document`)}
            name="documento"
            mask={cpfOrCnpjMask}
            required
            error={Boolean(documentError)}
            errorText={documentError}
            onInput={() => setDocumentError("")}
          />
          <Input
            ref={installmentInputRef}
            className={style.formInput}
            fullWidth
            label={t(`${keyI18n}.fields.installmentNumber`)}
            name="valor"
            mask={onlyAlphanumeric}
          />
          <Button
            className={style.submitButton}
            size="small"
            variant="tonal"
            type="submit"
            disabled={loading}
            icon={(
              <span className={`material-symbols-sharp ${style.searchIcon}`}>
                search
              </span>
            )}
          >
            {t(`${keyI18n}.actions.search`)}
          </Button>
        </Form>
      </section>
      {!hasSearched && !loading && (
        <div className={style.initialMessage}>
          <span
            className={`material-symbols-sharp ${style.initialMessageIcon}`}
            aria-hidden="true"
          >
            checkbook
          </span>
          <div className={style.initialMessageText}>
            <p className="pgm-headline-small">
              {t(`${keyI18n}.initialMessage.document`)}
            </p>
            <p className="pgm-headline-small">
              {t(`${keyI18n}.initialMessage.installmentNumber`)}
            </p>
          </div>
        </div>
      )}

      <div className={style.resultsContainer} aria-busy={loading}>
        {loading && (
          <div className={style.loadingState} role="status" aria-live="polite">
            <span className={style.loadingIndicator} aria-hidden="true" />
            <p className={`pgm-title-medium ${style.loadingText}`}>
              {t(`${keyI18n}.loading`)}
            </p>
          </div>
        )}
      </div>
      {hasSearchError && !loading && (
        <div className={style.initialMessage} role="alert" aria-live="assertive">
          <span
            className={`material-symbols-sharp ${style.initialMessageIcon}`}
            aria-hidden="true"
          >
            search_off
          </span>
          <div className={style.initialMessageText}>
            <p className="pgm-headline-small">
              {t(`${keyI18n}.errors.title`)}
            </p>
            <p className="pgm-headline-small">
              {t(`${keyI18n}.errors.description`)}
            </p>
          </div>
        </div>
      )}
      {data && (
        <div className={style.contributorSummary}>
          <div className={style.contributorData}>
            <strong className={`pgm-title-medium ${style.contributorIdentity}`}>
              {contributorName} - {cpfOrCnpjMask(contributorDocument).mask()}
            </strong>
            {!searchedByInstallment && (
              <span>
                <strong className="pgm-title-medium">
                  {t(`${keyI18n}.summary.activeInstallments`)}
                </strong>{" "}
                <span className={`pgm-body-large ${style.installmentCount}`}>
                  {data.results}
                </span>
              </span>
            )}
          </div>

          {paymentRequestWarning && (
            <div className={`${style.paymentRequestWarning} pgm-body-medium`} role="status">
              <span className={`material-symbols-sharp ${style.paymentRequestWarningIcon}`} aria-hidden="true">
                warning
              </span>
              <span>{paymentRequestWarning}</span>
            </div>
          )}
        </div>
      )}
      {data && data.payment_requests.map((paymentRequest) => (
        <ItemParcelamento
          key={paymentRequest.id}
          id={paymentRequest.id}
          numero={paymentRequest.code}
          data={paymentRequest.requested_at}
          valor={paymentRequest.balance}
          valorPago={paymentRequest.amount_paid_main}
          totalParcelas={paymentRequest.number_installments}
          status={paymentRequest.status}
          status_i18n={paymentRequest.status_pt_br}
          opened={searchedByInstallment}
        />
      ))}
    </Layout>
  );
}
